import Load from 'components/Load';
import { createNavigationalRoute } from 'modules/app/components/NavigationalLayout';
import React from 'react';
import { Route } from 'react-router-dom';
import { RouteDescriptor, createRouteHandle } from 'routing/types';
import basePath from 'utils/basePath';
import { isCordova } from 'utils/platform';

const AsyncExperienceContactHostSuccess = Load(
  () =>
    import(
      /* webpackChunkName: "experience-contact-host-form" */ 'modules/experience/containers/ContactHostSuccessContainer'
    ),
  {},
  true,
);
const AsyncExperienceContactHostSendingModal = Load(
  () =>
    import(
      /* webpackChunkName: "experience-contact-host-form" */ 'modules/experience/containers/ContactHostSendingModalContainer'
    ),
  {},
  true,
);
const AsyncExperienceContactHostForm = Load(
  () =>
    import(
      /* webpackChunkName: "experience-contact-host-form" */ 'modules/experience/containers/ContactFormContainer'
    ),
  {},
  true,
);
const AsyncExperienceProductPage = Load(
  () =>
    import(/* webpackChunkName: "experience-page" */ './ExperienceProductPage'),
  {},
  true,
);
const AsyncExperienceLocalContainer = Load(
  () =>
    import(
      /* webpackChunkName: "experience-local-picker" */ './ExperienceLocalContainer'
    ),
);
const AsyncExperienceReviewsPage = Load(
  () =>
    import(
      /* webpackChunkName: "experience-reviews-page" */ './components/ExperienceReviewsPage'
    ),
  {},
  true,
);

const AsyncLocalContactHostSuccess = Load(
  () =>
    import(
      /* webpackChunkName: "local-contact-host-form" */ 'modules/experience/components/PickLocal/modals/ContactHostSuccessContainer'
    ),
  {},
  true,
);
const AsyncLocalContactHostSendingModal = Load(
  () =>
    import(
      /* webpackChunkName: "local-contact-host-form" */ 'modules/experience/components/PickLocal/modals/ContactHostSendingModalContainer'
    ),
  {},
  true,
);
const AsyncLocalContactHostForm = Load(
  () =>
    import(
      /* webpackChunkName: "local-contact-host-form" */ 'modules/experience/components/PickLocal/modals/ContactFormContainer'
    ),
  {},
  true,
);

const AsyncHostVideoModal = Load(
  () =>
    import(
      /* webpackChunkName: "host-video-modal" */ './containers/HostVideoModalContainer'
    ),
  {},
  true,
);
const AsyncInstantlyConfirmedModal = Load(
  () =>
    import(
      /* webpackChunkName: "how-it-works-modal-exp-page" */ 'components/HowItWorksModal/InstantlyConfirmedModal'
    ),
);
const AsyncFamilyFriendlyHowItWorks = Load(
  () =>
    import(
      /* webpackChunkName: "how-it-works-modal-exp-page" */ 'components/HowItWorksModal/FamilyFriendlyHowItWorks'
    ),
);

export const experienceContactSuccessRoute: RouteDescriptor = {
  id: 'experience.contact.success',
  path: `${basePath}/experience/:id/contact/:hostId/:form/success/`,
  label: 'Experience Contact Success',
  audience: 'both',
  layout: 'standalone',
  module: 'experience',
};

export const experienceContactSendingRoute: RouteDescriptor = {
  id: 'experience.contact.sending',
  path: `${basePath}/experience/:id/contact/:hostId/:form/sending/`,
  label: 'Experience Contact Sending',
  audience: 'both',
  layout: 'modal',
  module: 'experience',
  modal: true,
};

export const experienceContactRoute: RouteDescriptor = {
  id: 'experience.contact.form',
  path: `${basePath}/experience/:id/contact/:hostId/:form/`,
  label: 'Experience Contact Form',
  audience: 'both',
  layout: 'standalone',
  module: 'experience',
};

export const localContactSuccessRoute: RouteDescriptor = {
  id: 'experience.local.contact.success',
  path: `${basePath}/experience/:id/locals/contact/:hostId/:form/success/`,
  label: 'Experience Local Contact Success',
  audience: 'both',
  layout: 'standalone',
  module: 'experience',
};

export const localContactSendingRoute: RouteDescriptor = {
  id: 'experience.local.contact.sending',
  path: `${basePath}/experience/:id/locals/contact/:hostId/:form/sending/`,
  label: 'Experience Local Contact Sending',
  audience: 'both',
  layout: 'modal',
  module: 'experience',
  modal: true,
};

export const localContactRoute: RouteDescriptor = {
  id: 'experience.local.contact.form',
  path: `${basePath}/experience/:id/locals/contact/:hostId/:form/`,
  label: 'Experience Local Contact Form',
  audience: 'both',
  layout: 'standalone',
  module: 'experience',
};

export const experienceLocalsRoute: RouteDescriptor = {
  id: 'experience.locals',
  path: `${basePath}/experience/:id/locals/*`,
  label: 'Experience Locals',
  audience: 'both',
  layout: 'standalone',
  module: 'experience',
};

export const experienceProductRoute: RouteDescriptor = {
  id: 'experience.product',
  path: `${basePath}/experience/:id/`,
  label: 'Experience Product',
  audience: 'both',
  layout: 'navigational',
  module: 'experience',
};

export const experienceReviewsRoute: RouteDescriptor = {
  id: 'experience.reviews',
  path: `${basePath}/experience/:id/reviews/`,
  label: 'Experience Reviews',
  audience: 'both',
  layout: 'navigational',
  module: 'experience',
};

export const experienceReviewsPaginatedRoute: RouteDescriptor = {
  id: 'experience.reviews.paginated',
  path: `${basePath}/experience/:id/reviews/page/:page/`,
  label: 'Experience Reviews Paginated',
  audience: 'both',
  layout: 'navigational',
  module: 'experience',
};

export const experienceVideoModalRoute: RouteDescriptor = {
  id: 'experience.modal.video',
  path: `${basePath}/experience/:id/video/:hostId/`,
  label: 'Experience Video Modal',
  audience: 'both',
  layout: 'modal',
  module: 'experience',
  modal: true,
};

export const experienceInstantlyConfirmedRoute: RouteDescriptor = {
  id: 'experience.modal.instantly-confirmed',
  path: `${basePath}/experience/instantly-confirmed/`,
  label: 'Experience Instantly Confirmed Modal',
  audience: 'both',
  layout: 'modal',
  module: 'experience',
  modal: true,
};

export const experienceFamilyFriendlyRoute: RouteDescriptor = {
  id: 'experience.modal.family-friendly',
  path: `${basePath}/experience/family-friendly/`,
  label: 'Experience Family Friendly Modal',
  audience: 'both',
  layout: 'modal',
  module: 'experience',
  modal: true,
};

const isApp = isCordova();

export const experienceRoutes = [
  createNavigationalRoute({
    key: experienceContactSuccessRoute.id,
    id: experienceContactSuccessRoute.id,
    path: experienceContactSuccessRoute.path,
    handle: createRouteHandle(experienceContactSuccessRoute),
    component: AsyncExperienceContactHostSuccess,
    showSearchInNavbar: true,
    noNavbar: isApp,
    hideFooter: true,
  }),
  <Route
    key={experienceContactSendingRoute.id}
    id={experienceContactSendingRoute.id}
    path={experienceContactSendingRoute.path}
    handle={createRouteHandle(experienceContactSendingRoute)}
    element={<AsyncExperienceContactHostSendingModal />}
  />,
  createNavigationalRoute({
    key: experienceContactRoute.id,
    id: experienceContactRoute.id,
    path: experienceContactRoute.path,
    handle: createRouteHandle(experienceContactRoute),
    component: AsyncExperienceContactHostForm,
    hideFooter: true,
  }),
  createNavigationalRoute({
    key: localContactSuccessRoute.id,
    id: localContactSuccessRoute.id,
    path: localContactSuccessRoute.path,
    handle: createRouteHandle(localContactSuccessRoute),
    component: AsyncLocalContactHostSuccess,
    showSearchInNavbar: true,
    hideFooter: true,
  }),
  <Route
    key={localContactSendingRoute.id}
    id={localContactSendingRoute.id}
    path={localContactSendingRoute.path}
    handle={createRouteHandle(localContactSendingRoute)}
    element={<AsyncLocalContactHostSendingModal />}
  />,
  createNavigationalRoute({
    key: localContactRoute.id,
    id: localContactRoute.id,
    path: localContactRoute.path,
    handle: createRouteHandle(localContactRoute),
    component: AsyncLocalContactHostForm,
    hideFooter: true,
  }),
  <Route
    key={experienceLocalsRoute.id}
    id={experienceLocalsRoute.id}
    path={experienceLocalsRoute.path}
    handle={createRouteHandle(experienceLocalsRoute)}
    element={<AsyncExperienceLocalContainer />}
  />,
  createNavigationalRoute({
    key: experienceReviewsRoute.id,
    id: experienceReviewsRoute.id,
    path: experienceReviewsRoute.path,
    handle: createRouteHandle(experienceReviewsRoute),
    component: AsyncExperienceReviewsPage,
    showSearchInNavbar: true,
    noNavbar: isApp,
  }),
  createNavigationalRoute({
    key: experienceReviewsPaginatedRoute.id,
    id: experienceReviewsPaginatedRoute.id,
    path: experienceReviewsPaginatedRoute.path,
    handle: createRouteHandle(experienceReviewsPaginatedRoute),
    component: AsyncExperienceReviewsPage,
    showSearchInNavbar: true,
    noNavbar: isApp,
  }),
  createNavigationalRoute({
    key: experienceProductRoute.id,
    id: experienceProductRoute.id,
    path: experienceProductRoute.path,
    handle: createRouteHandle(experienceProductRoute),
    component: AsyncExperienceProductPage,
    showSearchInNavbar: true,
    noNavbar: isApp,
    nonFixedNavbar: true,
  }),
  <Route
    key={experienceVideoModalRoute.id}
    id={experienceVideoModalRoute.id}
    path={experienceVideoModalRoute.path}
    handle={createRouteHandle(experienceVideoModalRoute)}
    element={<AsyncHostVideoModal />}
  />,
  <Route
    key={experienceInstantlyConfirmedRoute.id}
    id={experienceInstantlyConfirmedRoute.id}
    path={experienceInstantlyConfirmedRoute.path}
    handle={createRouteHandle(experienceInstantlyConfirmedRoute)}
    element={<AsyncInstantlyConfirmedModal />}
  />,
  <Route
    key={experienceFamilyFriendlyRoute.id}
    id={experienceFamilyFriendlyRoute.id}
    path={experienceFamilyFriendlyRoute.path}
    handle={createRouteHandle(experienceFamilyFriendlyRoute)}
    element={<AsyncFamilyFriendlyHowItWorks />}
  />,
];

export const experienceRouteDescriptors = [
  experienceContactSuccessRoute,
  experienceContactSendingRoute,
  experienceContactRoute,
  localContactSuccessRoute,
  localContactSendingRoute,
  localContactRoute,
  experienceLocalsRoute,
  experienceReviewsRoute,
  experienceReviewsPaginatedRoute,
  experienceProductRoute,
  experienceVideoModalRoute,
  experienceInstantlyConfirmedRoute,
  experienceFamilyFriendlyRoute,
];

export default experienceRoutes;
